import { create } from 'zustand';

/**
 * The operator chat mounted on the page, for surfaces that start runs
 * outside it. A canvas Run sends this conversation with the run so the
 * run's events (a node's clarifying question, a draft for review, the run
 * finishing) are relayed into the chat the user has open. When the rail is
 * on a new chat, `mint` gives the run an id the chat then adopts, so the
 * first thing in that conversation can be the run's own question.
 */
export interface OperatorConversationLink {
  agentId: string;
  conversationId: string | null;
  /** Creates and adopts a conversation id for the rail's new chat. */
  mint: () => string;
}

interface OperatorConversationState {
  link: OperatorConversationLink | null;
  setLink: (link: OperatorConversationLink | null) => void;
  /**
   * Bumped when a run needs the user's attention in the chat -- a question
   * just relayed -- so a host that has the rail collapsed can bring it back.
   */
  attentionSeq: number;
  requestAttention: () => void;
}

export const useOperatorConversationStore = create<OperatorConversationState>(
  (set) => ({
    link: null,
    setLink: (link) => set({ link }),
    attentionSeq: 0,
    requestAttention: () =>
      set((state) => ({ attentionSeq: state.attentionSeq + 1 })),
  }),
);

export interface RunChatLink {
  conversationId: string;
  agentId: string;
}

/**
 * The conversation a run started now should report to, minting one when the
 * rail has none. Null when no operator chat is mounted.
 */
export function linkRunToOperatorChat(): RunChatLink | null {
  const link = useOperatorConversationStore.getState().link;
  if (!link) return null;
  const conversationId = link.conversationId ?? link.mint();
  return { conversationId, agentId: link.agentId };
}
